// @flow
import * as React from 'react';
import styled, {keyframes} from 'styled-components';
import Image from 'components/Image';
import {LayoutWrapper, useLayoutState} from 'components/Layout';
import {LocalizedParagraph, useLocalized} from 'components/Localized';
import LocalizedImage from 'components/LocalizedImage';
import ButtonSpot from 'components/ButtonSpot';
import {SpotGuideMessage} from 'components/SpotMessage';
import GuideMessageContainer from 'components/GuideMessageContainer';
import GuideMessageCenter from 'components/GuideMessageCenter';
import * as AudioUtils from 'utils/audioUtils';
import ButtonNextPage from 'components/ButtonNextPage';

type Props = {
  completed?: boolean,
  onNext?: () => void,
};

function CreateImageLoadCallback(setImageLoaded, setImageNaturalSize) {
  return React.useCallback((e) => {
      setImageNaturalSize([e.target.naturalWidth, e.target.naturalHeight]);
      setTimeout(function(){
        setImageLoaded(true);
      }, 100);
    }, [setImageLoaded, setImageNaturalSize]);
}

const imageVisibilityRange = [
  [0, 9],
  [9, 11 ],
  [11, 13 ],
];


export default function DrivingResult(props: Props): React.Element<any> {
  const localized = useLocalized();
  const layoutState = useLayoutState();
  const [imageNaturalSize, setImageNaturalSize] = React.useState([0, 0]);
  const [imageSize, setImageSize] = React.useState([layoutState.width, layoutState.height, 0, 0]);
  const [guideIndex, setGuideIndex] = React.useState(-1);
  const nextTimer = React.useRef(null);
  const [imageLoaded0, setImageLoaded0] = React.useState(false);
  const [imageLoaded1, setImageLoaded1] = React.useState(false);
  const [imageLoaded4, setImageLoaded4] = React.useState(false);


  const handleLoad0 = CreateImageLoadCallback(setImageLoaded0, setImageNaturalSize);
  const handleLoad1 = CreateImageLoadCallback(setImageLoaded1, setImageNaturalSize);
  const handleLoad4 = CreateImageLoadCallback(setImageLoaded4, setImageNaturalSize);

  const handleShowNextButtonPage = React.useCallback((next?: number) => {
    if (guideIndex >= 11) {
      return;
    }
    
    const nextProvided = next != null && !isNaN(next);
    let nextPage = nextProvided ? next ?? guideIndex + 1 : guideIndex + 1;
    if (nextPage === 10) {
      nextPage = 11;
    }

    if (nextTimer.current) {
      clearTimeout(nextTimer.current);
      nextTimer.current = null;
    }

    let doNotReserve = false;
    if (nextPage === 3) {
      doNotReserve = true;
    }
    if (nextPage === 9) {
      doNotReserve = true;
      AudioUtils.play_car_welcome();

    }
    else if (nextPage === 5) {
      doNotReserve = true;
    }
    else if (nextPage === 7) {
      doNotReserve = true;
    }
    else if (nextPage === 10) {
      doNotReserve = true;
    }
    else if (nextPage >= 11) {
      doNotReserve = true;
    }


    setGuideIndex(nextPage);
    if (nextProvided && doNotReserve !== true) {

      nextTimer.current = setTimeout(() => handleShowNextButtonPage(nextPage + 1), 3000);
    }
  }, [guideIndex]);
  console.log("DrivingResult: guideIndex=", guideIndex);

  const handleShowNextButton = React.useCallback(() => {
    if (guideIndex !== 11) {
      
    }
    setGuideIndex(11);

    if (props.onNext) {
      if (nextTimer.current) {
        clearTimeout(nextTimer.current);
        nextTimer.current = null;
      }

      nextTimer.current = setTimeout(() => {
        nextTimer.current = null;
        setGuideIndex(12);
      }, 2000);
    }
  }, [props, guideIndex]);

  const handleRetry = React.useCallback(() => {
    if (nextTimer.current) {
      clearTimeout(nextTimer.current);
      nextTimer.current = null;
    }
    setGuideIndex(0);
  }, [])

  React.useEffect(() => {
    const imageNaturalWidth = imageNaturalSize[0] > 0 ? imageNaturalSize[0] : layoutState.width;
    const imageNaturalHeight = imageNaturalSize[1] > 0 ? imageNaturalSize[1] : layoutState.height;
    const hRatio = layoutState.height / imageNaturalHeight;
    const ratio = hRatio;
    const width = Math.round(imageNaturalWidth * ratio);
    const height = Math.round(imageNaturalHeight * ratio);

    setImageSize([
      width,
      height,
      Math.round((layoutState.width - width) * 0.5),
      Math.round((layoutState.height - height) * 0.5),
    ]);
  }, [layoutState, imageNaturalSize]);
    
  React.useEffect(() => {
    handleShowNextButtonPage(0);
    //eslint-disable-next-line
  }, [])

  return (
    <LayoutWrapper>
      <Back />
      <BackgroundWrapper
        datawidth={imageSize[0]}
        dataheight={imageSize[1]}
        dataleft={imageSize[2]}
        datatop={imageSize[3]}
        dataguide={guideIndex}
        >


        <ZoomParent id="zoomParent" dataguide={guideIndex}>
          { (guideIndex >= imageVisibilityRange[0][0] - 1 && guideIndex <= imageVisibilityRange[0][1]) ? (<Image onLoad={handleLoad0} source={"./driving-zone/img-gt-mode-off-bg.jpg"} style={{"visibility" : (imageLoaded0 ? "": "hidden") }} />) : (<div></div>) }
          { (guideIndex >= imageVisibilityRange[1][0] - 1 && guideIndex <= imageVisibilityRange[1][1]) ?  (<LocalizedImage onLoad={handleLoad1} source={"./driving-zone/img-gt-mode-on-bg.jpg"} style={{"visibility" : (imageLoaded1 ? "": "hidden") }} />) : (<div></div>) }
          { (guideIndex >= imageVisibilityRange[2][0] - 1 && guideIndex <= imageVisibilityRange[2][1]) ?  (<LocalizedImage onLoad={handleLoad4} source={"./driving-zone/img-gt-mode-gt-bg.jpg"} style={{"visibility" : (imageLoaded4 ? "": "hidden") }} />) : (<div></div>) }
        </ZoomParent>

      </BackgroundWrapper>
      <GuideWrapper
        datawidth={imageSize[0]}
        dataheight={imageSize[1]}
        dataleft={imageSize[2]}
        datatop={imageSize[3]}
        >
        <GuideMessageContainer onClick={handleShowNextButtonPage} rootEvents={true}>
          <GuideMessage2 show={guideIndex === 0}>
            <LocalizedParagraph {...localized.driving_a03_guide_001} />
            <LocalizedParagraph {...localized.driving_a03_guide_002} />
          </GuideMessage2>
          <GuideMessage2 show={guideIndex === 1}>
            <Image source="./driving-zone/img-ic-sound.png" />
            <LocalizedParagraph {...localized.driving_a03_guide_021} />
          </GuideMessage2>
          <GuideMessage2 show={guideIndex === 2}>
            <LocalizedParagraph {...localized.driving_a03_guide_011} />
          </GuideMessage2>
          <GuideMessage2 show={guideIndex === 6}>
            <LocalizedParagraph {...localized.driving_a03_guide_023} />
          </GuideMessage2>
          <GuideMessage2 show={guideIndex === 9}>
            <LocalizedParagraph {...localized.driving_a03_guide_014} />
          </GuideMessage2>
          <GuideMessage2 show={guideIndex === 11}>
            <LocalizedParagraph {...localized.driving_a03_guide_024} />
          </GuideMessage2>
        </GuideMessageContainer>
      {guideIndex === 3 && (
        <GuideDiv>
          <ButtonSpot onClick={() => {
            window.tagManager.activateGTModeSetTheGTModeLinkClick();
            handleShowNextButtonPage(9);

          } }
          posX={imageSize[0] * 0.480}
          posY={imageSize[1] * 0.780}
            ignoreSafeArea={true}
            >
            <SpotGuideMessage localText={localized.driving_a00_guide1_extra_021}/>
          </ButtonSpot>
        </GuideDiv>
      )}
      {guideIndex === 9 && (
        <GuideDiv>
          <ButtonSpot onClick={ ()=>{
            window.tagManager.activateGTModeGTModeActivatedLinkClick();
            handleShowNextButton();
          } }
            posX={imageSize[0] * 0.34}
            posY={imageSize[1] * 0.64}
            ignoreSafeArea={true}
            >
          </ButtonSpot>
        </GuideDiv>
      )}

      </GuideWrapper>
      {guideIndex >= 11 && <ButtonNextPage id="buttonNext" buttons={[{onNext: handleRetry, title:localized.driving_a02_result_005, dataBackground:"#05141f", dataOutline:"#ffffff"}, {onNext: props.onNext, title: localized.button_next}]}/>}

    </LayoutWrapper>
  );
}

const Back = styled.div`
  width: 100%;
  height: 100%;
  background-color: #000000;
`;

const BackgroundWrapper = styled.div`
  width: ${(props) => props.datawidth}px;
  height: ${(props) => props.dataheight}px;
  position: absolute;
  left: ${(props) => props.dataleft}px;
  top: ${(props) => props.datatop}px;

   > img {
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0px;
    top: 0px;
    transition: opacity 1s;
    pointer-events: none;
  }
`;

const GuideWrapper = styled.div`
  width: ${(props) => props.datawidth}px;
  height: ${(props) => props.dataheight}px;
  position: absolute;
  left: ${(props) => props.dataleft}px;
  top: ${(props) => props.datatop}px;
`;

const GuideMessage2 = styled(GuideMessageCenter)`
  flex-direction: column;
  
  img {
    width: ${(props) => (props.theme.layout.mobile ? 65 : 90)}px;
    height: ${(props) => (props.theme.layout.mobile ? 65 : 90)}px;
    padding-bottom: ${(props) => (props.theme.layout.mobile ? 30 : 40)}px;
  }
  p {
    text-align: center;
  }
`;

const GuideDiv = styled.div`
  width: 100%;
  height: 100%;
  pointer-events: auto;
  position: relative;
`;

const NoZoom = keyframes`
`;

const ZoomIn = keyframes`
  from {
    transform: scale(1);
  }
  to {
    transform: scale(2);
  }
`;

const ZoomOut = keyframes`
  from {
    transform: scale(2);
  }
  to {
    transform: scale(1);
  }
`;

const ZoomParent = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0px;
  top: 0px;
   > img {
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0px;
    top: 0px;
    transition: opacity 1s;
    pointer-events: none;

  }
  transform-origin: 50% 45%;

  >:nth-child(1) {
    opacity: ${props => (props.dataguide >= imageVisibilityRange[0][0] && props.dataguide <= imageVisibilityRange[0][1]) ? 1 : 0}
  }
  >:nth-child(2) {
    opacity: ${props => (props.dataguide >= imageVisibilityRange[1][0] && props.dataguide <= imageVisibilityRange[1][1]) ? 1 : 0}
  }
  >:nth-child(3) {
    opacity: ${props => (props.dataguide >= imageVisibilityRange[2][0] && props.dataguide <= imageVisibilityRange[2][1]) ? 1 : 0}
  }
  //animation-name: ${props => (props.dataguide < 6) ? (NoZoom) : (props.dataguide < 9 ? ZoomIn :ZoomOut ) };

  animation-duration: 1s;
  animation-fill-mode: forwards;

`;
